/* eslint-disable id-length, react/jsx-no-bind, no-plusplus */
import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import Month from './month';
import Toolbar from './toolbar';
import './custom-calendar.scss';

// Replaces material-ui/styles/transitions.easeOut()
const easeOutTransition = 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms';

// Replaces material-ui/DatePicker/dateUtils functions
const addDays = (d, days) => {
  const newDate = new Date(d.getTime());
  newDate.setDate(d.getDate() + days);
  return newDate;
};

const addMonths = (d, months) => {
  const newDate = new Date(d.getTime());
  newDate.setMonth(d.getMonth() + months);
  return newDate;
};

const addYears = (d, years) => {
  const newDate = new Date(d.getTime());
  newDate.setFullYear(d.getFullYear() + years);
  return newDate;
};

const getFirstDayOfMonth = (d) => {
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

const getFirstDayOfWeek = () => {
  const now = new Date();
  return new Date(now.setDate(now.getDate() - now.getDay()));
};

const getDaysInMonth = (d) => {
  const resultDate = getFirstDayOfMonth(d);
  resultDate.setMonth(resultDate.getMonth() + 1);
  resultDate.setDate(resultDate.getDate() - 1);
  return resultDate.getDate();
};

const getWeekArray = (d, firstDayOfWeek) => {
  const dayArray = [];
  const daysInMonth = getDaysInMonth(d);
  const weekArray = [];
  let week = [];

  for (let i = 1; i <= daysInMonth; i++) {
    dayArray.push(new Date(d.getFullYear(), d.getMonth(), i));
  }

  const addWeek = (w) => {
    const emptyDays = 7 - w.length;
    for (let i = 0; i < emptyDays; i++) {
      if (weekArray.length) {
        w.push(null);
      } else {
        w.unshift(null);
      }
    }
    weekArray.push(w);
  };

  dayArray.forEach((day) => {
    if (week.length > 0 && day.getDay() === firstDayOfWeek) {
      addWeek(week);
      week = [];
    }
    week.push(day);
    if (dayArray.indexOf(day) === dayArray.length - 1) {
      addWeek(week);
    }
  });

  return weekArray;
};

const monthDiff = (d1, d2) => {
  let m = (d1.getFullYear() - d2.getFullYear()) * 12;
  m += d1.getMonth();
  m -= d2.getMonth();
  return m;
};

// Default utils object (replaces defaultUtils from dateUtils)
const defaultUtils = {
  addDays,
  addMonths,
  addYears,
  getFirstDayOfMonth,
  getWeekArray,
  monthDiff
};

// Replaces dateTimeFormat - uses native Intl.DateTimeFormat
const dateTimeFormat = Intl.DateTimeFormat;

// Replaces localizedWeekday from dateUtils
const localizedWeekday = (DateTimeFormat, locale, day, firstDayOfWeek) => {
  const weekdayFormatter = new DateTimeFormat(locale, { weekday: 'narrow' });
  const firstDayDate = getFirstDayOfWeek();
  return weekdayFormatter.format(addDays(firstDayDate, day + firstDayOfWeek));
};

const daysArray = [...Array(7)];

// Custom calendar component, based on the Material UI's DatePicker Calendar component.
const CustomCalendar = forwardRef(({
  DateTimeFormat = dateTimeFormat,
  dualPosition,
  exceptions,
  firstDayOfWeek = 1,
  locale = 'en-US',
  maxDate,
  minDate,
  onExcludeDate,
  onMonthChange: onMonthChangeProp,
  utils = defaultUtils,
  weekdays
}, ref) => {
  const isLeftCalendar = dualPosition === 'left';

  const getInitialDate = () => {
    const initialDate = minDate || new Date();
    if (isLeftCalendar) {
      return initialDate;
    }
    const firstDay = utils.getFirstDayOfMonth(initialDate);
    return utils.addMonths(firstDay, 1);
  };

  const [displayDate, setDisplayDate] = useState(() => utils.getFirstDayOfMonth(getInitialDate()));
  const [transitionDirection, setTransitionDirection] = useState('left');

  const getMinDate = useCallback(() => {
    return minDate || utils.addYears(new Date(), -100);
  }, [minDate, utils]);

  const getMaxDate = useCallback(() => {
    return maxDate || utils.addYears(new Date(), 100);
  }, [maxDate, utils]);

  const handleTouchTapDay = useCallback((event, date) => {
    if (onExcludeDate) {
      onExcludeDate(date);
    }
  }, [onExcludeDate]);

  // Exposed via ref for parent component to call (used by Exclusions component)
  const onMonthChange = useCallback((months) => {
    // isRtl is always false in this project
    const direction = months >= 0 ? 'left' : 'right';
    setTransitionDirection(direction);
    setDisplayDate(prevDate => utils.addMonths(prevDate, months));
  }, [utils]);

  // Expose onMonthChange via ref for parent to sync dual calendars
  useImperativeHandle(ref, () => ({
    onMonthChange
  }), [onMonthChange]);

  const getToolbarInteractions = () => {
    return {
      // Since it's a dual calendar, hide the next and previous month links for the first
      // and second calendars.
      showNextMonth: !isLeftCalendar,
      showPrevMonth: isLeftCalendar,
      // Then disable the links if we hit the min/max dates:
      prevMonth: utils.monthDiff(displayDate, getMinDate()) > 0,
      nextMonth: utils.monthDiff(displayDate, getMaxDate()) < 0
    };
  };

  const toolbarInteractions = getToolbarInteractions();

  return (
    <div styleName="custom-calendar-container">
      <div styleName="calendar-container">
        <div
          styleName="calendar"
          style={{ transition: easeOutTransition }}
        >
          <Toolbar
            DateTimeFormat={DateTimeFormat}
            locale={locale}
            displayDate={displayDate}
            onMonthChange={onMonthChangeProp}
            {...toolbarInteractions}
          />
          <div styleName="week-title">
            {daysArray.map((event, dayIndex) => (
              <span key={dayIndex} styleName="day">
                {localizedWeekday(DateTimeFormat, locale, dayIndex, firstDayOfWeek)}
              </span>
            ))}
          </div>
          <TransitionGroup
            style={{ height: 214, position: 'relative', overflow: 'hidden' }}
            childFactory={child => React.cloneElement(child, {
              classNames: `slide-${transitionDirection}`
            })}
          >
            <CSSTransition
              key={displayDate.toDateString()}
              timeout={450}
              classNames={`slide-${transitionDirection}`}
            >
              <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
                <Month
                  DateTimeFormat={DateTimeFormat}
                  exceptions={exceptions}
                  locale={locale}
                  displayDate={displayDate}
                  firstDayOfWeek={firstDayOfWeek}
                  minDate={minDate}
                  maxDate={maxDate}
                  onTouchTapDay={handleTouchTapDay}
                  utils={utils}
                  weekdays={weekdays}
                />
              </div>
            </CSSTransition>
          </TransitionGroup>
        </div>
      </div>
    </div>
  );
});

CustomCalendar.displayName = 'CustomCalendar';

CustomCalendar.propTypes = {
  DateTimeFormat: PropTypes.func,
  dualPosition: PropTypes.string,
  exceptions: PropTypes.array,
  firstDayOfWeek: PropTypes.number,
  locale: PropTypes.string,
  maxDate: PropTypes.object,
  minDate: PropTypes.object,
  onExcludeDate: PropTypes.func,
  onMonthChange: PropTypes.func,
  utils: PropTypes.object,
  weekdays: PropTypes.array
};

export default CustomCalendar;
